import React, { useState } from "react";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { api, apiError } from "@/lib/api";
import { Download, Loader2, Database, FolderArchive, Trash2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

// Datei-Export wird in Paketen geladen, damit kein einzelner Request vom
// Gateway-Timeout gekappt wird (das Holen jeder Datei aus dem Storage kostet Zeit).
const FILES_BATCH_SIZE = 50;

export default function AdminSystem() {
  const [exporting, setExporting] = useState(null); // null | "db" | "files"
  const [progress, setProgress] = useState("");
  const [cleanup, setCleanup] = useState({ busy: false, preview: null, aggressive: false });

  // Test-User Cleanup (Dry-Run + Hard-Delete)
  const previewCleanup = async () => {
    setCleanup((s) => ({ ...s, busy: true, preview: null }));
    try {
      const { data } = await api.post("/admin/cleanup/test-users", {
        dry_run: true, aggressive: cleanup.aggressive,
      });
      setCleanup((s) => ({ ...s, busy: false, preview: data }));
    } catch (e) {
      toast.error(apiError(e));
      setCleanup((s) => ({ ...s, busy: false }));
    }
  };

  const runCleanup = async () => {
    const n = cleanup.preview?.would_delete_users ?? 0;
    if (!n) { toast.info("Nichts zu löschen."); return; }
    if (!window.confirm(`Wirklich ${n} Test-Gutachter und alle zugehörigen Cases, Uploads, Rechnungen etc. UNWIDERRUFLICH löschen?`)) return;
    setCleanup((s) => ({ ...s, busy: true }));
    try {
      const { data } = await api.post("/admin/cleanup/test-users", {
        dry_run: false, confirm: "DELETE", aggressive: cleanup.aggressive,
      });
      toast.success(`${data.deleted_users} Test-User gelöscht`, {
        description: `Cases: ${data.deleted_case_ids_count} · Uploads: ${data.storage_files_removed}`,
      });
      setCleanup({ busy: false, preview: null, aggressive: false });
    } catch (e) {
      toast.error(apiError(e));
      setCleanup((s) => ({ ...s, busy: false }));
    }
  };

  // Hilfsfunktion: ein Blob als Datei herunterladen.
  const saveBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  // DB-Export: ein einzelnes ZIP.
  const exportDb = async () => {
    setExporting("db");
    try {
      const { data } = await api.get("/admin/system/export-db", { responseType: "blob", timeout: 300_000 });
      saveBlob(data, `easygutachter-export-${new Date().toISOString().slice(0, 10)}.zip`);
      toast.success("Datenbank-Export heruntergeladen");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setExporting(null);
    }
  };

  // Datei-Export: paginiert in mehreren Paketen (je FILES_BATCH_SIZE Dateien),
  // jedes Paket wird als eigenes ZIP heruntergeladen.
  const exportFiles = async () => {
    setExporting("files");
    setProgress("Ermittle Datenmenge…");
    try {
      const { data: count } = await api.get("/admin/system/export-storage/count", { timeout: 60_000 });
      const total = count.total || 0;
      if (total === 0) {
        toast.info("Keine Dateien zum Exportieren gefunden.");
        return;
      }
      const batches = Math.ceil(total / FILES_BATCH_SIZE);
      const date = new Date().toISOString().slice(0, 10);
      for (let i = 0; i < batches; i++) {
        const offset = i * FILES_BATCH_SIZE;
        setProgress(`Lade Paket ${i + 1}/${batches}…`);
        const { data } = await api.get(
          `/admin/system/export-storage?offset=${offset}&limit=${FILES_BATCH_SIZE}`,
          { responseType: "blob", timeout: 300_000 },
        );
        saveBlob(data, `easygutachter-files-${date}-${String(offset).padStart(4, "0")}.zip`);
        // kurze Pause, damit der Browser die Downloads nacheinander verarbeitet
        await new Promise((s) => setTimeout(s, 800));
      }
      toast.success(`${batches} Datei-Pakete heruntergeladen (${total} Dateien). Bitte alle aufbewahren.`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setExporting(null);
      setProgress("");
    }
  };

  return (
    <Layout title="System" subtitle="Systemverwaltung & Wartung">
      <section className="rounded-2xl border border-zinc-200 bg-white p-6">
        <h2 className="text-lg font-semibold text-zinc-900 mb-1 flex items-center gap-2">
          <Database className="h-5 w-5 text-zinc-500" />
          Datenbank
        </h2>
        <p className="text-sm text-zinc-500 mb-4">
          Exportiert alle Collections der MongoDB als ZIP-Datei mit JSON-Dateien.
        </p>
        <Button onClick={exportDb} disabled={!!exporting} variant="outline" size="sm">
          {exporting === "db" ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Download className="h-4 w-4 mr-2" />
          )}
          {exporting === "db" ? "Exportiere…" : "Datenbank exportieren"}
        </Button>
      </section>

      <section className="rounded-2xl border border-zinc-200 bg-white p-6 mt-6">
        <h2 className="text-lg font-semibold text-zinc-900 mb-1 flex items-center gap-2">
          <FolderArchive className="h-5 w-5 text-zinc-500" />
          Dateien
        </h2>
        <p className="text-sm text-zinc-500 mb-4">
          Exportiert alle gespeicherten Dateien (Fotos, Dokumente, Unterschriften, Profilbilder)
          in mehreren ZIP-Paketen (je {FILES_BATCH_SIZE} Dateien). Jedes Paket enthält ein
          <code> _manifest.json</code> mit Erfolgs- und Fehlerliste. Bitte alle heruntergeladenen
          Pakete aufbewahren.
        </p>
        <Button onClick={exportFiles} disabled={!!exporting} variant="outline" size="sm">
          {exporting === "files" ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Download className="h-4 w-4 mr-2" />
          )}
          {exporting === "files" ? (progress || "Exportiere…") : "Dateien exportieren"}
        </Button>
      </section>

      {/* ─── Test-User Cleanup ───────────────────────────────────────── */}
      <section className="rounded-2xl border border-rose-200 bg-rose-50/40 p-6 mt-6" data-testid="admin-cleanup-section">
        <h2 className="text-lg font-semibold text-zinc-900 mb-1 flex items-center gap-2">
          <ShieldAlert className="h-5 w-5 text-rose-600" />
          Test-Gutachter aufräumen
        </h2>
        <p className="text-sm text-zinc-600 mb-4 leading-relaxed">
          Löscht automatisch generierte Test-Accounts (E-Mails wie <code>test_*@example.com</code>,{" "}
          <code>reset_*</code>, <code>lockout_*</code>, <code>gdv-fb-*</code>, <code>mobtest*</code>,{" "}
          <code>probe*</code>, <code>test_stripe_*</code>) samt aller Cases, Uploads, Rechnungen,
          Sessions und Audit-Logs. <strong>Bewahrt</strong> werden alle Admin/Backoffice-Konten,
          echte Kunden (@gmail, @firma etc.) sowie die Demo-Accounts
          <code> gutachter@kfz-gutachter.de</code> und <code>locked-test@kfz-gutachter.de</code>.
        </p>
        <label className="inline-flex items-center gap-2 text-xs text-zinc-600 mb-4 cursor-pointer">
          <input
            type="checkbox"
            checked={cleanup.aggressive}
            onChange={(e) => setCleanup((s) => ({ ...s, aggressive: e.target.checked, preview: null }))}
            className="h-4 w-4 rounded border-zinc-300"
            data-testid="admin-cleanup-aggressive-toggle"
          />
          Aggressiv: <em>alle</em> Gutachter mit <code>@example.com</code> / <code>@example.de</code> löschen (auch außerhalb der Standard-Patterns)
        </label>
        <div className="flex flex-wrap items-center gap-3">
          <Button
            onClick={previewCleanup}
            disabled={cleanup.busy}
            variant="outline"
            size="sm"
            data-testid="admin-cleanup-preview-btn"
          >
            {cleanup.busy && !cleanup.preview ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <ShieldAlert className="h-4 w-4 mr-2" />
            )}
            {cleanup.preview ? "Neu prüfen" : "Vorschau (Dry-Run)"}
          </Button>
          {cleanup.preview && (
            <Button
              onClick={runCleanup}
              disabled={cleanup.busy || !cleanup.preview.would_delete_users}
              variant="destructive"
              size="sm"
              data-testid="admin-cleanup-run-btn"
            >
              {cleanup.busy ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              {cleanup.preview.would_delete_users} User + Daten löschen
            </Button>
          )}
        </div>
        {cleanup.preview && (
          <div className="mt-4 rounded-lg border border-zinc-200 bg-white p-4 text-xs text-zinc-700" data-testid="admin-cleanup-preview">
            <div className="font-semibold text-zinc-900 mb-2">
              Vorschau: {cleanup.preview.would_delete_users} Gutachter würden gelöscht
            </div>
            {cleanup.preview.would_delete_users > 0 && (
              <>
                <div className="mb-2">
                  <span className="font-semibold">Beispiele:</span>{" "}
                  {(cleanup.preview.sample_emails || []).slice(0, 5).join(", ")}
                  {cleanup.preview.would_delete_users > 5 ? ` … +${cleanup.preview.would_delete_users - 5} weitere` : ""}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-y-1 gap-x-4">
                  {Object.entries(cleanup.preview.impact || {}).filter(([_, v]) => v > 0).map(([k, v]) => (
                    <div key={k}>
                      <span className="text-zinc-500">{k}:</span> <span className="font-semibold">{v}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </section>
    </Layout>
  );
}
